import { useCallback, useEffect, useRef, useState } from "react";

interface UseCountDownProps {
  initialValue: number;
  intervalMs?: number;
  countUp?: boolean;
  limit: number;
  onComplete?: () => void;
}

export const useCountDown = ({
  initialValue,
  intervalMs = 1000,
  countUp = false,
  limit,
  onComplete,
}: UseCountDownProps) => {
  const [count, setCount] = useState(initialValue);
  const [isRunning, setIsRunning] = useState(false);
  const [restartCount, setRestartCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (isRunning) return;
    setIsRunning(true);
  }, [isRunning]);

  const stop = useCallback(() => {
    if (!isRunning) return;
    setIsRunning(false);
    clearTimer();
  }, [isRunning, clearTimer]);

  const restart = useCallback(() => {
    clearTimer();
    setCount(initialValue);
    setRestartCount(prev => prev + 1);
    setIsRunning(true);
  }, [initialValue, clearTimer]);

  useEffect(() => {
    if (!isRunning) return;

    timerRef.current = setInterval(() => {
      setCount(prev => {
        const newValue = countUp ? prev + intervalMs : prev - intervalMs;
        if ((countUp && newValue >= limit) || (!countUp && newValue <= limit)) {
          clearTimer();
          setIsRunning(false);
          onComplete?.();
          return limit;
        }
        return newValue;
      });
    }, intervalMs);

    return () => clearTimer();
    // onComplete is invoked at the limit edge; callers often pass an inline fn
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- clearTimer is stable enough; onComplete intentionally omitted
  }, [isRunning, countUp, limit, intervalMs, clearTimer]);

  return { count, isRunning, restartCount, start, stop, restart };
};
